import dayjs from 'dayjs';

import { ToCronExpressionInput } from '@/shared/types/transform.types';

const formatTimeOfDay = (hours: number, minutes: number): string =>
  `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

type TWeeklySchedule = {
  days: number[];
  timeOfDay: string;
};

// Anchor the run in its own timezone before converting. Reading an instant from
// the other zone first rolls the calendar date whenever the zones straddle
// midnight, which shifts the weekday.
const shiftScheduleBetweenZones = (
  { days, timeOfDay }: TWeeklySchedule,
  fromZone: string,
  toZone: string,
): TWeeklySchedule => {
  // day(0) is Sunday independent of the configured locale week start, which
  // the 1-7 day numbering here relies on.
  const weekStartInFromZone = dayjs().tz(fromZone).day(0);
  const daysToProcess = days.length ? days : ALL_WEEKDAYS;

  const toTargetZone = (dayOfWeek: number) => {
    const dateInFromZone = weekStartInFromZone
      .add(dayOfWeek - 1, 'day')
      .format('YYYY-MM-DD');

    return dayjs.tz(`${dateInFromZone} ${timeOfDay}`, fromZone).tz(toZone);
  };

  const shiftedDays: number[] = [];
  for (const dayOfWeek of daysToProcess) {
    const shiftedDay = toTargetZone(dayOfWeek).day() + 1;

    if (!shiftedDays.includes(shiftedDay)) {
      shiftedDays.push(shiftedDay);
    }
  }

  const shiftedTime = toTargetZone(daysToProcess[0]);

  return {
    days: days.length ? shiftedDays.sort((a, b) => a - b) : [],
    timeOfDay: formatTimeOfDay(shiftedTime.hour(), shiftedTime.minute()),
  };
};

export const toCronExpression = (
  configuration: ToCronExpressionInput,
): string => {
  const { days, timeOfDay } = configuration;

  if (!days || !timeOfDay) {
    return 'cron(0 0 ? * * *)'; // Default cron expression
  }

  const [hours, minutes] = timeOfDay
    .split(':')
    .map((part) => parseInt(part, 10));

  const cronDays = days.sort((a, b) => a - b);
  const dayOfWeekExpression = cronDays.length > 0 ? cronDays.join(',') : '*';

  return `cron(${minutes} ${hours} ? * ${dayOfWeekExpression} *)`;
};

// The form edits a schedule in the viewer timezone, so writing back to a
// schedule that belongs to another timezone has to undo that conversion.
export const toCronExpressionInTimezone = (
  configuration: ToCronExpressionInput,
  expressionTimezone: string,
): string => {
  const { days, timeOfDay } = configuration;

  if (!days || !timeOfDay) {
    return toCronExpression(configuration);
  }

  return toCronExpression(
    shiftScheduleBetweenZones(
      { days, timeOfDay },
      dayjs.tz.guess(),
      expressionTimezone,
    ),
  );
};

// A schedule the editor did not touch must be written back byte-identical:
// re-deriving it converts through a week anchored in the editor timezone, which
// lands on a different offset than the read did across a DST boundary.
export const isSameSchedule = (
  a: ToCronExpressionInput,
  b: ToCronExpressionInput,
): boolean =>
  a.timeOfDay === b.timeOfDay &&
  [...(a.days ?? [])].sort((x, y) => x - y).join(',') ===
    [...(b.days ?? [])].sort((x, y) => x - y).join(',');

export const fromCronExpression = (
  cronExpression: string,
  expressionTimezone?: string,
): ToCronExpressionInput | null => {
  const cronRegex = /^cron\((\d+)\s+(\d+)\s+\?\s+\*\s+([1-7,*]+)\s+\*\)$/;
  const match = cronExpression.match(cronRegex);

  if (!match) {
    return null;
  }

  const [, minutesStr, hoursStr, daysOfWeekStr] = match;
  const timeOfDay = formatTimeOfDay(
    parseInt(hoursStr, 10),
    parseInt(minutesStr, 10),
  );

  const isEveryDay = daysOfWeekStr === '*';
  const days = isEveryDay
    ? []
    : daysOfWeekStr.split(',').map((day) => parseInt(day, 10));

  if (!expressionTimezone) {
    return { days: [...days].sort((a, b) => a - b), timeOfDay };
  }

  return shiftScheduleBetweenZones(
    { days, timeOfDay },
    expressionTimezone,
    dayjs.tz.guess(),
  );
};

export const snakeToCamelCase = <T>(obj: T): T => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce<Record<string, any>>((result, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );

    const _key = key as keyof T;

    const value =
      typeof obj[_key] === 'object' && obj[_key] !== null
        ? snakeToCamelCase(obj[_key])
        : obj[_key];

    result[camelKey] = value;
    return result;
  }, {}) as T;
};
